import {
  GET_SEARCH_IMAGES_SUCCESS,
  GetSearchImagesSuccessAction,
  SET_QUICK_IMAGE_SEARCH_TAG,
  SetQuickImageSearchTagAction
} from 'js/actionCreators/ImageDrawerActions';
import {
  CLEAR_SEARCH_INPUT,
  RESET_SEARCH,
  ResetSearchAction,
  ClearSearchInputAction,
  SEARCH_IMAGES,
  SearchImagesAction,
  SET_SEARCH_INPUT,
  SET_SELECTED_CATEGORY,
  SetSearchInputAction,
  SetSelectedCategoryAction,
  ToggleSearchFilterAction,
  TOGGLE_SEARCH_FILTER,
  CLEAR_ALL_FILTERS,
  ClearAllFiltersAction
} from 'js/actionCreators/imageSearchActions';
import { EDITOR_CLOSE, EditorCloseAction } from 'js/actionCreators/scribeActions';
import { FacetsList } from 'js/types';

export enum SearchType {
  INPUT = 'search',
  CATEGORY = 'category'
}

export enum ImageLibraryStep {
  CATEGORIES,
  SEARCH_RESULT
}

export type FacetFilterConfig = {
  [facetId: string]: string[];
};

export interface ImageSearchState {
  searchInput: string;
  submittedSearchInput: string;
  searchType: SearchType;
  selectedCategoryId: string | null;
  displayedStep: ImageLibraryStep;
  facets: FacetsList;
  selectedFilters: FacetFilterConfig;
}

const initialState: ImageSearchState = {
  searchInput: '',
  submittedSearchInput: '',
  searchType: SearchType.INPUT,
  selectedCategoryId: null,
  displayedStep: ImageLibraryStep.CATEGORIES,
  facets: [],
  selectedFilters: {}
};

type ActionType =
  | SetSearchInputAction
  | ClearSearchInputAction
  | SearchImagesAction
  | SetSelectedCategoryAction
  | ResetSearchAction
  | SetQuickImageSearchTagAction
  | GetSearchImagesSuccessAction
  | ToggleSearchFilterAction
  | ClearAllFiltersAction
  | EditorCloseAction;

export default function imageSearchReducer(state = initialState, action: ActionType) {
  switch (action.type) {
    case SET_SEARCH_INPUT: {
      return {
        ...state,
        searchInput: action.searchInput
      };
    }

    case CLEAR_SEARCH_INPUT: {
      return {
        ...state,
        searchInput: ''
      };
    }

    case SEARCH_IMAGES: {
      return {
        ...state,
        submittedSearchInput: state.searchInput,
        searchType: SearchType.INPUT,
        selectedCategoryId: null,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        selectedFilters: {},
        facets: []
      };
    }

    case SET_QUICK_IMAGE_SEARCH_TAG: {
      return {
        ...state,
        searchInput: action.tag,
        submittedSearchInput: action.tag,
        searchType: SearchType.INPUT,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        selectedCategoryId: null,
        selectedFilters: {},
        facets: []
      };
    }

    case SET_SELECTED_CATEGORY: {
      return {
        ...state,
        selectedCategoryId: action.selectedCategoryId,
        searchType: SearchType.CATEGORY,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        searchInput: '',
        submittedSearchInput: '',
        selectedFilters: {},
        facets: []
      };
    }

    case EDITOR_CLOSE:
    case RESET_SEARCH: {
      return {
        ...state,
        selectedCategoryId: null,
        searchInput: '',
        submittedSearchInput: '',
        searchType: SearchType.INPUT,
        displayedStep: ImageLibraryStep.CATEGORIES,
        selectedTag: null,
        selectedFilters: {},
        facets: []
      };
    }

    case GET_SEARCH_IMAGES_SUCCESS: {
      if (action.imageSource !== 'videoscribe' || !action.facets) return state;

      if (!state.facets.length) {
        return {
          ...state,
          facets: action.facets || []
        };
      }

      const newFacets = structuredClone(state.facets);
      newFacets.forEach(facet => {
        const incomingFacet = action.facets?.find(f => f.id === facet.id);

        facet.values.forEach(facetValue => {
          facetValue.disabled = false;

          if (!incomingFacet) {
            facetValue.disabled = true;
            return;
          }

          const incomingFacetValue = incomingFacet.values.find(fv => fv.value === facetValue.value);

          if (!incomingFacetValue) {
            facetValue.disabled = true;
            return;
          }
          facetValue.count = incomingFacetValue.count;

          if (facetValue.count === 0) {
            facetValue.disabled = true;
          }
        });
      });

      return {
        ...state,
        facets: newFacets
      };
    }

    case TOGGLE_SEARCH_FILTER: {
      const { facetId, facetValue } = action;

      if (!state.selectedFilters[facetId]) {
        return {
          ...state,
          selectedFilters: {
            ...state.selectedFilters,
            [facetId]: [facetValue]
          }
        };
      } else {
        if (state.selectedFilters[facetId].includes(facetValue)) {
          return {
            ...state,
            selectedFilters: {
              ...state.selectedFilters,
              [facetId]: state.selectedFilters[facetId].filter(val => val !== facetValue)
            }
          };
        } else {
          return {
            ...state,
            selectedFilters: {
              ...state.selectedFilters,
              [facetId]: state.selectedFilters[facetId].concat([facetValue])
            }
          };
        }
      }
    }

    case CLEAR_ALL_FILTERS: {
      return {
        ...state,
        selectedFilters: {}
      };
    }

    default:
      return state;
  }
}
